import React from 'react';
import { 
  LayoutDashboard, 
  Users, 
  X,
  LogOut,
  ClipboardList,
  FolderOpen,
  UserCog,
  Wallet,
  Search,
  Settings,
  FilePenLine,
  Cable,
  BookOpenCheck,
} from 'lucide-react';
import BrandLockup from '../branding/BrandLockup';

// Menu items com indicação de permissão
const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, adminOnly: false },
  { id: 'tarefas', label: 'Tarefas', icon: ClipboardList, adminOnly: false },
  { id: 'clientes', label: 'Clientes', icon: Users, adminOnly: true },
  { id: 'questor', label: 'Operação LBCC', icon: BookOpenCheck, adminOnly: true },
  { id: 'documentos', label: 'Documentos', icon: FolderOpen, adminOnly: true },
  { id: 'colaboradores', label: 'Equipe', icon: UserCog, adminOnly: true },
  { id: 'financeiro', label: 'Financeiro', icon: Wallet, adminOnly: true },
  { id: 'consultas', label: 'Consultas', icon: Search, adminOnly: true },
  { id: 'editor-documento', label: 'Editor LBCC', icon: FilePenLine, adminOnly: true },
  { id: 'sieg', label: 'SIEG', icon: Cable, adminOnly: true },
  // Certificados movido para central do cliente
  // Certidões removido (busca manual não usada)
];

export default function Sidebar({ currentPage, setCurrentPage, user, onLogout, mobileOpen = false, onCloseMobile }) {
  const isAdmin = user?.role === 'admin';
  
  // Filtrar menus baseado no role
  const visibleMenuItems = menuItems.filter(item => !item.adminOnly || isAdmin);
  
  return (
    <>
      {mobileOpen && (
        <button
          type="button"
          aria-label="Fechar menu"
          className="fixed inset-0 z-40 bg-slate-950/60 lg:hidden"
          onClick={onCloseMobile}
          data-testid="mobile-sidebar-overlay"
        />
      )}

      <aside className={`lbcc-system-sidebar fixed left-0 top-0 z-50 h-screen w-[280px] max-w-[85vw] text-white flex flex-col transition-transform duration-300 lg:w-64 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
      {/* Logo */}
      <div className="border-b border-white/10 p-4 lg:p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BrandLockup
              surface="dark"
              compact
              title="LBCCorex HUB"
              subtitle="Inteligência que move negócios"
              logoClassName="h-11 w-11"
              dataTestId="sidebar-brand-lockup"
            />
          </div>
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/15 text-slate-300 lg:hidden"
            onClick={onCloseMobile}
            data-testid="mobile-sidebar-close-button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Menu */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-3">
          {visibleMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;
            return (
              <li key={item.id}>
                <button
                  onClick={() => {
                    if (item.href) {
                      onCloseMobile?.();
                      window.location.assign(item.href);
                      return;
                    }
                    setCurrentPage(item.id);
                    onCloseMobile?.();
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive 
                      ? 'bg-emerald-500 text-[#071B30] shadow-lg shadow-emerald-500/25'
                      : 'text-slate-300 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`sidebar-nav-${item.id}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          {user?.picture ? (
            <img 
              src={user.picture} 
              alt={user.name || 'Usuário'}
              className="w-10 h-10 rounded-full object-cover border-2 border-emerald-500"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center ${isAdmin ? 'bg-emerald-500 text-[#071B30]' : 'bg-white/10'}`}
            style={{ display: user?.picture ? 'none' : 'flex' }}
          >
            <span className="text-sm font-bold">{user?.name?.charAt(0) || 'U'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name || 'Usuário'}</p>
            <p className="text-xs text-slate-400 truncate">
              {isAdmin ? 'Administrador' : 'Colaborador'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <button 
              onClick={() => {
                setCurrentPage('config');
                onCloseMobile?.();
              }}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
              data-testid="sidebar-nav-config"
            >
              <Settings className="w-4 h-4" />
              <span>Config</span>
            </button>
          )}
          <button 
            onClick={() => {
              onCloseMobile?.();
              onLogout();
            }}
            className={`${isAdmin ? 'flex-1' : 'w-full'} flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-300 hover:text-red-200 hover:bg-white/10 rounded-lg transition-colors`}
          >
            <LogOut className="w-4 h-4" />
            <span>Sair</span>
          </button>
        </div>
      </div>
      </aside>
    </>
  );
}
